<template>
    <div
        :class="[
            'call-controlls',
            callIsActive ? 'call-controlls--active' : 'call-controlls--inactive'
        ]">
        <div class="call-controlls__info call-controlls__info__number">{{ callNumber }}</div>
        <div class="call-controlls__icons display--flex">
            <transition name="fade" mode="out-in">
                <div v-if="callIsActive" class="call-controlls__call display--flex">
                    <div class="position--relative svg--parent"
                        @mouseover="showRecord = true"
                        @mouseleave="showRecord = false"
                    >
                        <button-with-icon
                            class="call-controlls__icon"
                            :action="{
                                execute: true,
                                function: () => $parent.$emit( 'record-call' )
                            }"
                        >
                            <template #icon-with-animation>
                                <icon-stop-record-call :isRecording="disableRecording" />
                            </template>
                        </button-with-icon>
                        <tooltip
                            v-show="showRecord"
                            class="call-controlls__icon__tooltip--record"
                            direction="bottom"
                            animation="bottom--base"
                            v-html=" disableRecording ? $t('global.record') : $t('global.stopRecording')"
                        />
                    </div>
                    <div
                        class="position--relative svg--parent"
                        @mouseover="showPause = true"
                        @mouseleave="showPause = false"
                    >
                        <button-with-icon
                            class="call-controlls__icon"
                            :action="{
                                execute: true,
                                function: () => $parent.$emit( 'hold-call' )
                            }"
                        >
                            <template #icon-with-animation>
                                <icon-media-control :isPause="isOnHold" />
                            </template>
                        </button-with-icon>
                        <tooltip
                            v-show="showPause"
                            class="call-controlls__icon__tooltip--pause"
                            direction="bottom"
                            animation="bottom--base"
                            v-html=" isOnHold ? $t('global.leaveOnHold') : $t('global.putOnHold')"
                        />
                    </div>
                    <div
                        class="position--relative svg--parent"
                        @mouseover="showDialer = true"
                        @mouseleave="showDialer = false"
                    >
                         <dialer />
                        <tooltip
                            v-show="showDialer"
                            class="call-controlls__icon__tooltip--dialer"
                            direction="bottom"
                            animation="bottom--base"
                            v-html=" $t('global.openDialer')"
                        />
                    </div>
                    <div
                        class="position--relative svg--parent"
                        @mouseover="showStop = true"
                        @mouseleave="showStop = false"
                    >
                        <button-with-icon
                            :icon="{
                                component: () => import(
                                    /* webpackChunkName: 'IconHangUp' */ '@/components/bundles/layout/images/icons/IconHangUp'
                                )
                            }"
                            class="call-controlls__icon"
                            :action="{
                                execute: true,
                                function: () => $parent.$emit( 'stop-call' )
                            }"
                        />
                        <tooltip
                            v-show="showStop"
                            class="call-controlls__icon__tooltip--hangup"
                            direction="bottom"
                            animation="bottom--base"
                            v-html=" $t('global.hangUpCall')"
                        />
                    </div>
                </div>
                <div
                    v-else
                    class="position--relative svg--parent call-typification"
                    @mouseover="showTypification = true"
                    @mouseleave="showTypification = false"
                 >
                    <button-with-icon
                        :icon="{
                            component: () => import(
                                /* webpackChunkName: 'IconDone' */ '@/components/bundles/layout/images/icons/IconDone'
                            )
                        }"
                        class="call-controlls__icon"
                        :action="{
                            execute: !blockTypification,
                            function: () => $parent.$emit( 'type-call' )
                        }"
                    />
                    <tooltip
                        v-show="showTypification"
                        class="call-controlls__icon__tooltip--typification"
                        direction="bottom"
                        animation="bottom--base"
                        v-html=" $t('global.duplicateContactCall')"
                    />
                </div>
            </transition>
        </div>
        <transition name="fade" mode="out-in">
            <counter
                v-if="callHasBeenConfirmed && callIsActive"
                :class="[
                    'call-controlls__info',
                    isOnHold ? 'color--grey' : 'color--green'
                ]"
                :timeLimit="0"
                :stop="isOnHold"
            />
            <div v-else-if="callIsActive" class="call-controlls__info">--:--</div>
            <div v-else class="call-controlls__info call-controlls__warning position--relative capitalize">
                <icon-hang-up-simple class="call-controlls__warning__hang position--absolute" />
                {{ $t('global.theCallEnded') }}
            </div>
        </transition>
        <transition name="fade" mode="out-in">
            <counter
                v-if="isOnHold && callIsActive"
                class="call-controlls__pause position--absolute color--red"
                :timeLimit="0"
            />
        </transition>
    </div>
</template>

<script>
// packages
import { reactive, toRefs } from '@vue/composition-api';

export default {
    components: {
        Tooltip: () => import( /* webpackChunkName: 'Tooltips' */ '@/components/bundles/tooltips/Tooltip' ),
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' ),
        Counter: () => import( /* webpackChunkName: 'Counter' */ '@/components/bundles/operate/blocks/Counter' ),
        Dialer: () => import( /* webpackChunkName: 'Dialer' */ '@/components/bundles/operate/blocks/Dialer' ),
        IconMediaControl: () => import( /* webpackChunkName: 'IconMediaControl' */ '@/components/bundles/layout/images/icons/IconMediaControl' ),
        IconHangUpSimple: () => import( /* webpackChunkName: 'IconHangUpSimple' */ '@/components/bundles/layout/images/icons/IconHangUpSimple' ),
        IconStopRecordCall: () => import( /* webpackChunkName: 'IconStopRecordCall' */ '@/components/bundles/layout/images/icons/IconStopRecordCall' )
    },
    props: {
        callIsActive: Boolean,
        callHasBeenConfirmed: Boolean,
        callNumber: String,
        isOnHold: Boolean,
        disableRecording: Boolean,
        blockTypification: Boolean
    },
    setup () {
        const tooltip = reactive({
            showRecord: false,
            showDialer: false,
            showStop: false,
            showPause: false,
            showTypification: false
        });

        return {
            ...toRefs( tooltip )
        };
    }
};
</script>

<style lang="scss">
    .call-controlls {
        &--active {
            margin-left: -40px;
        }
        &__icons {
            justify-content: center;
            min-height: 95px;
        }
        &__call {
            justify-content: center;
            align-items: center;
        }
        &__icon {
            width: auto;
            will-change: transform;
            &--middle {
                margin: 0 30px 0 20px;
            }
            &:hover {
                @include transform( scale( 1.2 ) );
            }
            &__tooltip{
                &--record {
                    @include transform-position( 50px, 35px, false, false );
                }
                &--dialer {
                    @include transform-position( 85px, 96px, false, false );
                }
                &--hangup {
                    @include transform-position( 82px, 67px, false, false );
                }
                &--pause {
                    @include transform-position( 92px, 75px, false, false );
                }
                &--typification {
                    @include transform-position( 78px, 72px, false, false );
                }
            }
        }
        &__info {
            width: max-content;
            text-align: center;
            @include typography-specs( 22px, 29px );
            padding-left: 25px;
            margin: 12px auto 0;
            &__number {
                margin: 0 auto 12px;
                padding-left: 0;
                font-weight: 600;
            }
        }
        &__pause {
            bottom: 2px;
            padding-left: 25px;
        }
        &__warning {
            padding-left: 50px;
            &__hang {
                @include transform-position( 1px, false, false, 0 );
            }
        }
    }
    .call-controlls__call .tooltip, .call-typification .tooltip {
        padding: 10px;
        min-width: max-content;
        &--bottom {
                &::before {
                    width: 20px;
                    @include transform-position( -6px, -6px, false, false, rotate( 100deg ) translateX( 30% ) skew( 55deg ) );
                }
            }
    }

</style>